import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ChevronDown, Laptop2, Phone, Smartphone, XCircle } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { Modal } from "../../components/ui/modal";
import { core } from "../../lib/api";
import { canPlaceTelCall } from "../../lib/utils";
import { contactIdOf, type ComplianceMatch, type Lead } from "./types";
import { crmApi } from "./api";
import { ManualCallModal } from "./manual-call-modal";
import { SimCallLogModal } from "./sim-call-log-modal";
import { ComplianceBlockedModal } from "./compliance-blocked-modal";

const DEFAULT_TRIGGER_CLS =
  "flex items-center gap-1.5 rounded-lg bg-brand text-white hover:bg-brand-hover shadow-xs px-2.5 py-1 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-50 transition-colors";

/** The Web/Desk-call vs My-phone(SIM) call dropdown, plus its modals — shared
 * by the lead detail drawer and the leads table row, so both stay in sync
 * with the same phone-presence guard and call-logging flow instead of two
 * copies drifting apart. */
export function LeadCallButton({ lead, onLogged, triggerClassName, label }: { lead: Lead; onLogged?: () => void; triggerClassName?: string; label?: string }) {
  const [simNotice, setSimNotice] = useState<{ ok: boolean; title: string; body: string } | null>(null);
  const [blockedMatches, setBlockedMatches] = useState<ComplianceMatch[] | null>(null);
  const [sendingSim, setSendingSim] = useState(false);
  const [callingLead, setCallingLead] = useState<Lead | null>(null);
  const [simLogLead, setSimLogLead] = useState<Lead | null>(null);
  // True only when this click actually opened the native dialer (mobile).
  // On desktop there is no dialer — the modal still opens for manual logging.
  const [simAutoDialed, setSimAutoDialed] = useState(false);
  useEffect(() => { setBlockedMatches(null); setSimNotice(null); }, [lead.id]);
  const queryClient = useQueryClient();
  const invalidateCallHistory = () => {
    void queryClient.invalidateQueries({ queryKey: ["crm-history", lead.id] });
    void queryClient.invalidateQueries({ queryKey: ["crm-follow-ups"] });
    // The history query caches per contact; without this a call that just
    // ended never appears until the rep hits Refresh manually.
    void queryClient.invalidateQueries({ queryKey: ["crm-contact-calls", contactIdOf(lead)] });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            onClick={(e) => e.stopPropagation()}
            disabled={!lead.phone}
            title={!lead.phone ? "This contact has no phone number on file" : undefined}
            className={triggerClassName ?? DEFAULT_TRIGGER_CLS}
          >
            <Phone className="h-3.5 w-3.5" /> {label ?? "Call"} <ChevronDown className="h-3 w-3 opacity-70" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56 rounded-xl border border-line bg-elevated p-1.5 shadow-lg z-50">
          <DropdownMenuItem
            onClick={() => setCallingLead({ ...lead })}
            className="flex cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-ink-2 hover:bg-soft hover:text-ink focus:bg-soft focus:text-ink"
          >
            <Laptop2 className="h-4 w-4" />
            Web / Desk call
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={sendingSim}
            onClick={async () => {
              // Pre-dial DND gate — applies to both the native dialer and the
              // queued request. Blocked attempts are audited into history.
              if (lead.phone) {
                try {
                  const comp = await crmApi.complianceCheck(lead.phone);
                  if (comp.suppressed) {
                    setBlockedMatches(comp.matches);
                    try {
                      await crmApi.log(lead.id, {
                        id: crypto.randomUUID(), kind: "compliance",
                        notes: `Dial blocked — ${comp.matches.map(m => m.reason).join(", ") || "suppression list"}`,
                      });
                      invalidateCallHistory();
                    } catch { /* audit is best-effort */ }
                    return;
                  }
                } catch { /* check failed — fail open to the normal flow */ }
              }
              const mobile = canPlaceTelCall();
              if (!mobile) {
                setSendingSim(true);
                try {
                  await core(`/api/v1/crm/leads/${lead.id}/sim-request`, { method: "POST" });
                  setSimNotice({
                    ok: true,
                    title: "Check your phone",
                    body: `Open the PWA on your phone (same login) and accept to call ${lead.name || lead.phone} via SIM. The request expires in 2 minutes.`,
                  });
                } catch (e) {
                  setSimNotice({
                    ok: false,
                    title: "Couldn't send call request",
                    body: e instanceof Error ? e.message : "Unable to send call request",
                  });
                }
                finally { setSendingSim(false); }
                return;
              }
              setSimAutoDialed(mobile);
              if (mobile) window.location.href = `tel:${lead.phone}`;
              setSimLogLead({ ...lead });
            }}
            className="flex cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-ink-2 hover:bg-soft hover:text-ink focus:bg-soft focus:text-ink"
          >
            <Smartphone className="h-4 w-4" />
            My phone (SIM)
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {simNotice && (
        <Modal
          title={simNotice.title}
          onClose={() => setSimNotice(null)}
          width="max-w-sm"
          footer={
            <button onClick={() => setSimNotice(null)} className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover">
              Got it
            </button>
          }
        >
          <div className="flex items-start gap-3">
            {simNotice.ok
              ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
              : <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />}
            <p className="text-sm text-ink-2">{simNotice.body}</p>
          </div>
        </Modal>
      )}
      {blockedMatches && (
        <ComplianceBlockedModal
          leadName={lead.name || lead.phone || "This lead"}
          matches={blockedMatches}
          onClose={() => setBlockedMatches(null)}
        />
      )}
      {callingLead && <ManualCallModal lead={callingLead} onClose={() => setCallingLead(null)} onLog={() => {
        invalidateCallHistory();
        onLogged?.();
        setCallingLead(null);
      }} />}
      {simLogLead && <SimCallLogModal lead={simLogLead} autoDialed={simAutoDialed} onClose={() => setSimLogLead(null)} onLog={() => {
        invalidateCallHistory();
        onLogged?.();
        setSimLogLead(null);
      }} />}
    </>
  );
}
